import type { jsPDF } from "jspdf";
import {
  buildGraphicRenderPlan,
  getLayerGraphic,
  hexToRgb,
  normRectToPdfMm,
  type SurfLayer,
  type SurfStyle,
} from "@vida/surf-core";

export function drawGraphicLayer(
  pdf: jsPDF,
  layer: SurfLayer,
  theme: SurfStyle,
  rect: ReturnType<typeof normRectToPdfMm>,
): void {
  const plan = buildGraphicRenderPlan(getLayerGraphic(layer), theme);
  const mapX = (n: number) => rect.x + n * rect.w;
  const mapY = (n: number) => rect.y + n * rect.h;
  const mapW = (n: number) => n * rect.w;
  const mapH = (n: number) => n * rect.h;

  for (const box of plan.rects) {
    const [r, g, b] = hexToRgb(box.fill.length === 9 ? box.fill.slice(0, 7) : box.fill);
    pdf.setFillColor(r, g, b);
    pdf.roundedRect(mapX(box.x), mapY(box.y), mapW(box.w), mapH(box.h), 1, 1, "F");
  }

  for (const line of plan.lines) {
    const [r, g, b] = hexToRgb(line.stroke);
    pdf.setDrawColor(r, g, b);
    pdf.setLineWidth((line.width ?? 1) * 0.12);
    pdf.line(mapX(line.x1), mapY(line.y1), mapX(line.x2), mapY(line.y2));
  }

  for (const circle of plan.circles) {
    const [r, g, b] = hexToRgb(circle.fill);
    pdf.setFillColor(r, g, b);
    pdf.circle(mapX(circle.cx), mapY(circle.cy), mapW(circle.r), "F");
  }

  for (const t of plan.texts) {
    const [r, g, b] = hexToRgb(t.fill);
    pdf.setTextColor(r, g, b);
    pdf.setFontSize(Math.max(6, (t.size ?? 0.04) * rect.h * 2.8));
    const x = t.anchor === "middle" ? mapX(t.x + t.w / 2) : mapX(t.x);
    pdf.text(t.text, x, mapY(t.y + (t.size ?? 0.04)), { align: t.anchor === "middle" ? "center" : "left" });
    if (t.subtext) {
      const [sr, sg, sb] = hexToRgb(t.subFill ?? t.fill);
      pdf.setTextColor(sr, sg, sb);
      pdf.setFontSize(Math.max(5, (t.subSize ?? 0.03) * rect.h * 2.5));
      pdf.text(t.subtext, x, mapY(t.y + (t.size ?? 0.04) + (t.subSize ?? 0.03)), {
        align: t.anchor === "middle" ? "center" : "left",
      });
    }
  }

  void layer.rotation;
}
